import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  PropsWithChildren,
  useMemo,
} from "react";
import { useLocation } from "react-router";
import { graphql } from "~/graphql/codegen";
import { useQuery } from "@apollo/client/react";
import {
  type AiUsageLastLogAndDismissedDateQuery,
  type AiUsageLastLogAndDismissedDateQueryVariables,
} from "~/graphql/codegen/graphql";
import { usePlatformAccess } from "~/hooks/platform-access";
import metrics from "~/utils/metrics";

const AI_USAGE_LAST_LOG_AND_DISMISSED_DATE_QUERY = graphql(`
  query AiUsageLastLogAndDismissedDate {
    currentUser {
      aiUsage {
        lastLogDate
        shouldShowTimeToLogUsageDialog
      }
    }
    me {
      store {
        aiUsageLastDismissedAt
      }
    }
  }
`);

export type AIUsageContextType = {
  showAIUsagePopover: boolean;
  setShowAIUsagePopover: (show: boolean) => void;
};

const AIUsageContext = createContext<AIUsageContextType | undefined>(undefined);

export const AIUsageProvider: React.FC<PropsWithChildren<{}>> = ({
  children,
}) => {
  const [showAIUsagePopover, setShowAIUsagePopover] = useState(false);
  const hasTrackedShown = useRef(false);
  // Entitlement = active Plus billing OR the self-serve policy (see
  // ~/hooks/platform-access). The query itself is only authenticated, never
  // billing-gated — this skip was pure client presentation, and it hid the
  // AI-usage nudge from self-serve learners, who have a metered message cap
  // and so need it more than anyone. Strictly ADDITIVE: the hook only ORs a
  // second term on, so a lapsed-Plus Live learner still skips as before.
  const hasPlatformAccess = usePlatformAccess();
  const location = useLocation();

  const shouldHideAIUsagePopoverOnPath = useMemo(() => {
    const regexPatterns = [
      // The Rebel launch bridge is a single-purpose page (open-in-rebel-learning plan).
      /^\/launch\//,
      /\/sandbox/,
      /\/annotate\/article_/,
      /\/cohorts/,
      /\/programs\/[^\/]+\/cohort_[a-zA-Z0-9]+/,
      /\/organization/,
    ];

    return regexPatterns.some((pattern) => pattern.test(location.pathname));
  }, [location.pathname]);

  const { data } = useQuery<
    AiUsageLastLogAndDismissedDateQuery,
    AiUsageLastLogAndDismissedDateQueryVariables
  >(AI_USAGE_LAST_LOG_AND_DISMISSED_DATE_QUERY, {
    // Without the platform entitlement, never show the popover.
    skip: !hasPlatformAccess,
  });

  // Set initial value from backend flag
  useEffect(() => {
    const flag = data?.currentUser?.aiUsage?.shouldShowTimeToLogUsageDialog;

    if (
      flag !== undefined &&
      flag !== showAIUsagePopover &&
      !shouldHideAIUsagePopoverOnPath
    ) {
      setShowAIUsagePopover(flag);
    }
  }, [data, hasPlatformAccess, shouldHideAIUsagePopoverOnPath]);

  // Track metric when shown
  useEffect(() => {
    if (showAIUsagePopover && !hasTrackedShown.current) {
      metrics.track("ai_usage_popover_shown");
      hasTrackedShown.current = true;
    }
  }, [showAIUsagePopover]);

  return (
    <AIUsageContext.Provider
      value={{ showAIUsagePopover, setShowAIUsagePopover }}
    >
      {children}
    </AIUsageContext.Provider>
  );
};

export const useAIUsage = () => {
  const context = useContext(AIUsageContext);
  if (context === undefined) {
    throw new Error("useAIUsage must be used within an AIUsageProvider");
  }
  return context;
};
